/**
 * CustomFieldsBuilder — add/edit/reorder custom intake fields.
 * Operators can extend the booking intake form per vertical, and every
 * value drops into bookings.custom_fields jsonb, queryable for reports.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, GripVertical, Pencil, Lock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';

const FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'boolean', 'select', 'multiselect', 'file'];

export default function CustomFieldsBuilder({ api, auth }) {
  const [fields, setFields] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    axios.get(`${api}/api/booking/fields`, auth()).then((r) => setFields(r.data || [])).catch(() => {});
  };
  useEffect(load, [api]);

  const remove = async (f) => {
    if (f.system) { toast.error('System-seeded field — edit its visibility instead'); return; }
    if (!window.confirm(`Delete field "${f.label}"?`)) return;
    await axios.delete(`${api}/api/booking/fields/${f.id}`, auth());
    toast.success('Field deleted');
    load();
  };

  return (
    <div className="space-y-4" data-testid="fields-builder">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-medium">
          Every value from these fields is saved to <code>bookings.custom_fields</code> and shows in Reports.
        </p>
        <Button onClick={() => setEditing({ __new: true, type: 'text', options: [] })} className="gap-1.5" size="sm" data-testid="add-field-btn">
          <Plus className="h-4 w-4" /> Add Field
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-gray-medium text-center py-12">No custom fields yet. Run Setup to install a template, or add one manually.</p>
      ) : (
        <div className="space-y-2">
          {fields.map((f) => (
            <Card key={f.id} data-testid={`field-row-${f.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <GripVertical className="h-4 w-4 text-gray-300" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{f.label}</span>
                      <Badge variant="outline" className="text-xs">{f.type}</Badge>
                      {f.required && <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">required</Badge>}
                      {f.system && <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600"><Lock className="h-3 w-3 mr-1" />seeded</Badge>}
                    </div>
                    <div className="text-xs text-gray-medium mt-0.5">
                      key: <code className="text-xs">{f.key}</code>
                      {f.options && f.options.length > 0 && <> · options: {f.options.slice(0, 4).join(', ')}{f.options.length > 4 ? '…' : ''}</>}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(f)} data-testid={`edit-field-${f.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {!f.system && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(f)} data-testid={`delete-field-${f.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <FieldEditor
          field={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          api={api}
          auth={auth}
        />
      )}
    </div>
  );
}


function FieldEditor({ field, onClose, onSaved, api, auth }) {
  const [form, setForm] = useState({
    key: field.key || '',
    label: field.label || '',
    type: field.type || 'text',
    required: !!field.required,
    unit: field.unit || '',
    options: (field.options || []).join('\n'),
    visible_in_reports: field.visible_in_reports !== false,
  });

  const save = async () => {
    const payload = {
      key: form.key.trim(),
      label: form.label.trim(),
      type: form.type,
      required: form.required,
      unit: form.unit,
      visible_in_reports: form.visible_in_reports,
    };
    if (['select', 'multiselect'].includes(form.type)) {
      payload.options = form.options.split('\n').map((x) => x.trim()).filter(Boolean);
    }
    if (!payload.key || !payload.label) { toast.error('Key and label required'); return; }
    try {
      if (field.__new) {
        await axios.post(`${api}/api/booking/fields`, payload, auth());
        toast.success('Field created');
      } else {
        await axios.patch(`${api}/api/booking/fields/${field.id}`, payload, auth());
        toast.success('Field updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="field-editor-dialog">
        <DialogHeader><DialogTitle>{field.__new ? 'Add Field' : `Edit "${field.label}"`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Key</Label>
              <Input
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value.replace(/\s+/g, '_').toLowerCase() })}
                disabled={!field.__new}
                placeholder="e.g. allergies"
                data-testid="field-key"
              />
              <p className="text-xs text-gray-medium mt-1">DB column identifier — cannot change later.</p>
            </div>
            <div>
              <Label>Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Known Allergies" data-testid="field-label" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="field-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} data-testid="field-required" />
                Required
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.visible_in_reports} onChange={(e) => setForm({ ...form, visible_in_reports: e.target.checked })} />
                Show in Reports
              </label>
            </div>
          </div>
          {form.type === 'number' && (
            <div><Label>Unit (optional)</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="e.g. kg, cm, $" /></div>
          )}
          {['select', 'multiselect'].includes(form.type) && (
            <div>
              <Label>Options (one per line)</Label>
              <textarea
                className="w-full border rounded-md px-3 py-2 text-sm font-mono"
                rows={4}
                value={form.options}
                onChange={(e) => setForm({ ...form, options: e.target.value })}
                placeholder={'Option 1\nOption 2\nOption 3'}
                data-testid="field-options"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-emerald-brand" data-testid="save-field-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
